import { useEffect, useMemo, useRef, useState } from "react";

import {
  DEG,
  HEAT_CELL,
  KIND_COLOR,
  PLAN_MARGIN as MARGIN,
  cameraBlockers,
  heatCells,
  heatColor,
  viewPath,
} from "../lib/planogram";
import type { Planogram } from "../types";

/** Read-only floor plan with the heat projected onto the floor grid: every
 *  cell a camera actually sees — inside its cone and not cut off by a wall or
 *  view-blocking fixture — is colored by the hottest seeing camera's metric
 *  value on a cool→hot ramp. Cells seen only by cameras without data in the
 *  window render as a faint neutral fill so coverage gaps are visible, not
 *  invisible; floor no camera sees stays bare. The cameras themselves —
 *  clipped view region, marker, label, aim tick — are an overlay drawn only
 *  with `showCameras` on; by default the map is just the colored grid over
 *  the plan. */

const LABEL = "pointer-events-none select-none fill-text font-sans";

interface PlanogramHeatmapProps {
  plan: Planogram;
  /** camera marker id → metric value for the selected window/metric. */
  values: Record<string, number>;
  /** Normalization maximum (the hottest camera's value); ≤0 means no data. */
  max: number;
  format: (value: number) => string;
  /** Overlay the cameras (view cone, marker, label) on the heat grid. */
  showCameras?: boolean;
}

export function PlanogramHeatmap({
  plan,
  values,
  max,
  format,
  showCameras = false,
}: PlanogramHeatmapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  // Metres per screen pixel — keeps text a constant on-screen size.
  const [k, setK] = useState(0.02);

  const { width, height, fixtures, walls, cameras } = plan;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const update = () => {
      const w = svg.getBoundingClientRect().width;
      if (w > 0) setK((width + 2 * MARGIN) / w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [width]);

  // Hotter cones draw on top so overlaps stay readable.
  const ordered = [...cameras].sort(
    (a, b) => (values[a.id] ?? -1) - (values[b.id] ?? -1),
  );

  const cells = useMemo(() => heatCells(plan, values), [plan, values]);

  // What each camera actually sees, clipped by walls and blocking fixtures.
  const views = useMemo(
    () =>
      new Map(
        cameras.map((c) => [c.id, viewPath(c, cameraBlockers(c, walls ?? [], fixtures))]),
      ),
    [cameras, walls, fixtures],
  );

  return (
    <div className="max-w-[960px] overflow-hidden rounded-card border border-border bg-surface leading-[0]">
      <svg
        ref={svgRef}
        className="block h-auto w-full"
        viewBox={`${-MARGIN} ${-MARGIN} ${width + 2 * MARGIN} ${height + 2 * MARGIN}`}
      >
        <defs>
          <pattern id="heat-grid" width={1} height={1} patternUnits="userSpaceOnUse">
            <path
              d="M 1 0 L 0 0 0 1"
              fill="none"
              stroke="var(--border)"
              strokeWidth={k}
              opacity={0.7}
            />
          </pattern>
        </defs>

        <rect x={0} y={0} width={width} height={height} fill="var(--bg)" />
        {cells.map((cell) => {
          const hot = cell.value !== null && max > 0;
          const t = hot ? (cell.value as number) / max : 0;
          return (
            <rect
              key={`${cell.x},${cell.y}`}
              x={cell.x}
              y={cell.y}
              width={Math.min(HEAT_CELL, width - cell.x)}
              height={Math.min(HEAT_CELL, height - cell.y)}
              fill={hot ? heatColor(t) : "var(--muted)"}
              fillOpacity={hot ? 0.35 + 0.45 * t : 0.08}
            />
          );
        })}
        <rect x={0} y={0} width={width} height={height} fill="url(#heat-grid)" />
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="none"
          stroke="var(--text)"
          strokeWidth={3}
          vectorEffect="non-scaling-stroke"
          opacity={0.75}
        />

        {fixtures.map((f) => (
          <g key={f.id}>
            <rect
              x={f.x}
              y={f.y}
              width={f.w}
              height={f.h}
              fill={KIND_COLOR[f.kind]}
              fillOpacity={f.kind === "zone" ? 0.04 : 0.1}
              stroke={KIND_COLOR[f.kind]}
              strokeOpacity={0.6}
              strokeWidth={1.25}
              strokeDasharray={f.kind === "zone" ? `${6 * k} ${4 * k}` : undefined}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={f.x + f.w / 2}
              y={f.y + f.h / 2}
              textAnchor="middle"
              dominantBaseline="central"
              className={LABEL}
              fontSize={10 * k}
              opacity={0.75}
            >
              {f.label || f.kind}
            </text>
          </g>
        ))}

        {(walls ?? []).map((w) => (
          <line
            key={w.id}
            x1={w.x1}
            y1={w.y1}
            x2={w.x2}
            y2={w.y2}
            stroke="var(--text)"
            strokeWidth={4}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={0.85}
          />
        ))}

        {showCameras &&
          ordered.map((c) => {
            const value = values[c.id];
            const hasData = value !== undefined && max > 0;
            const color = hasData ? heatColor(value / max) : "var(--muted)";
            // The grid carries the heat; the outline is the region the camera
            // actually sees (cone clipped by walls and blocking fixtures),
            // tinted by its value.
            const coneProps = hasData
              ? {
                  fill: color,
                  fillOpacity: 0.08,
                  stroke: color,
                  strokeOpacity: 0.9,
                }
              : {
                  fill: "var(--muted)",
                  fillOpacity: 0.04,
                  stroke: "var(--muted)",
                  strokeOpacity: 0.6,
                  strokeDasharray: `${5 * k} ${4 * k}`,
                };
            return (
              <g key={c.id}>
                <path
                  d={views.get(c.id) ?? ""}
                  strokeWidth={1.25}
                  vectorEffect="non-scaling-stroke"
                  {...coneProps}
                />
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={6.5 * k}
                  fill={color}
                  stroke="var(--bg)"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={c.x}
                  y={c.y - 12 * k}
                  textAnchor="middle"
                  className={LABEL}
                  fontSize={11 * k}
                  fontWeight={600}
                >
                  {(c.label || c.id) +
                    " · " +
                    (hasData ? format(value) : "no data")}
                </text>
                {/* aim tick so orientation reads even on tiny cones */}
                <line
                  x1={c.x}
                  y1={c.y}
                  x2={c.x + Math.cos(c.rotation * DEG) * Math.min(c.range, 14 * k)}
                  y2={c.y + Math.sin(c.rotation * DEG) * Math.min(c.range, 14 * k)}
                  stroke="var(--bg)"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}
      </svg>
    </div>
  );
}
